"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import "./DoctorHeaderCard.css";
import { resolveDoctorAvatar } from "../../../hooks/useDoctorInformation";
import { useDoctorFollow } from "../../../hooks/useDoctorFollow";
import ROUTES from "../../../../routes/patientRoutes/routePaths";
import { MESSAGING_ROUTES } from "../../../../messagerie/routePaths";

const normalizeSpecialtyKey = (value) => {
  if (!value) return null;
  const norm = value.toString().trim().toLowerCase();
  if (!norm) return null;

  if (norm.includes("dermatolog")) return "dermatology";
  if (norm.includes("pediatr")) return "pediatrics";
  if (norm.includes("cardiolog")) return "cardiology";
  if (norm.includes("neurolog")) return "neurology";
  if (norm.includes("psychiatr")) return "psychiatry";
  if (norm.includes("ophthalmolog") || norm.includes("ophtalmolog")) return "ophthalmology";
  if (norm.includes("orthop")) return "orthopedics";
  if (norm.includes("gynecolog") || norm.includes("gynaecolog")) return "gynecology";
  if (norm.includes("dentist")) return "dentistry";
  if (norm.includes("general")) return "general";
  if (norm === "ent" || (norm.includes("ear") && norm.includes("nose") && norm.includes("throat"))) return "ent";
  return null;
};

const getInitials = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Dr";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getDoctorId = (doctor) => {
  const candidates = [
    doctor?.doctorId,
    doctor?.id,
    doctor?._id,
    doctor?.userId,
    doctor?.pgId,
    doctor?.externalId,
    doctor?.raw?.doctorId,
    doctor?.raw?.id,
    doctor?.raw?._id,
    doctor?.raw?.userId,
    doctor?.raw?.pgId,
    doctor?.raw?.externalId,
  ];

  const value = candidates.find(
    (candidate) => candidate != null && String(candidate).trim() !== ""
  );
  return value == null ? null : String(value).trim();
};

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14.6667 4.6665L8.67268 8.4845C8.46927 8.60265 8.23824 8.66487 8.00301 8.66487C7.76779 8.66487 7.53675 8.60265 7.33334 8.4845L1.33334 4.6665"
      stroke="#546881"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3333 2.6665H2.66668C1.9303 2.6665 1.33334 3.26346 1.33334 3.99984V11.9998C1.33334 12.7362 1.9303 13.3332 2.66668 13.3332H13.3333C14.0697 13.3332 14.6667 12.7362 14.6667 11.9998V3.99984C14.6667 3.26346 14.0697 2.6665 13.3333 2.6665Z"
      stroke="#546881"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clipPath="url(#clip0_1_16587)">
      <path
        d="M8.66669 1.3335C10.258 1.3335 11.7841 1.96564 12.9093 3.09086C14.0345 4.21607 14.6667 5.7422 14.6667 7.3335"
        stroke="#546881"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.66669 4C9.55074 4 10.3986 4.35119 11.0237 4.97631C11.6488 5.60143 12 6.44928 12 7.33333"
        stroke="#546881"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.22131 11.0455C9.359 11.1087 9.51411 11.1232 9.66111 11.0865C9.8081 11.0497 9.9382 10.964 10.03 10.8435L10.2666 10.5335C10.3908 10.3679 10.5519 10.2335 10.737 10.1409C10.9222 10.0484 11.1263 10.0002 11.3333 10.0002H13.3333C13.6869 10.0002 14.0261 10.1406 14.2761 10.3907C14.5262 10.6407 14.6666 10.9799 14.6666 11.3335V13.3335C14.6666 13.6871 14.5262 14.0263 14.2761 14.2763C14.0261 14.5264 13.6869 14.6668 13.3333 14.6668C10.1507 14.6668 7.09847 13.4025 4.84803 11.1521C2.5976 8.90167 1.33331 5.84943 1.33331 2.66683C1.33331 2.31321 1.47379 1.97407 1.72384 1.72402C1.97389 1.47397 2.31302 1.3335 2.66665 1.3335H4.66665C5.02027 1.3335 5.35941 1.47397 5.60946 1.72402C5.8595 1.97407 5.99998 2.31321 5.99998 2.66683V4.66683C5.99998 4.87382 5.95179 5.07797 5.85922 5.26311C5.76665 5.44825 5.63224 5.6093 5.46665 5.7335L5.15465 5.9675C5.03226 6.06095 4.94599 6.19389 4.91051 6.34373C4.87502 6.49357 4.8925 6.65108 4.95998 6.7895C5.8711 8.64007 7.3696 10.1367 9.22131 11.0455Z"
        stroke="#546881"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_1_16587">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const MessageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M2.992 16.3417C3.13904 16.7126 3.17177 17.119 3.086 17.5087L2.021 20.7987C1.98668 20.9655 1.99555 21.1384 2.04677 21.3008C2.09799 21.4633 2.18986 21.61 2.31366 21.727C2.43747 21.844 2.58911 21.9274 2.7542 21.9693C2.91929 22.0113 3.09236 22.0104 3.257 21.9667L6.67 20.9687C7.03771 20.8958 7.41852 20.9276 7.769 21.0607C9.90438 22.0579 12.3234 22.2689 14.5991 21.6564C16.8749 21.0439 18.8613 19.6473 20.2077 17.7131C21.5541 15.7788 22.1741 13.4311 21.9583 11.0842C21.7425 8.73738 20.7047 6.54216 19.0281 4.88589C17.3514 3.22962 15.1437 2.21873 12.7944 2.03159C10.4451 1.84445 8.10513 2.49308 6.18744 3.86303C4.26975 5.23299 2.89753 7.23624 2.31289 9.51933C1.72825 11.8024 1.96877 14.2186 2.992 16.3417Z"
      fill="#006eea"
      stroke="#EDF9FE"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const NotPatientPopup = ({ onClose, onContinue, doctorName }) => {
  const { t } = useTranslation();
  return (
    <div className="mi-popup-overlay" onClick={onClose}>
      <div className="mi-popup" onClick={(e) => e.stopPropagation()}>
        <button className="mi-popup-close" onClick={onClose} aria-label="Fermer">
          <CloseIcon />
        </button>
        <div className="mi-popup-icon-wrap">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h4 className="mi-popup-title">{t("notPatientPopup.title")}</h4>
        <p className="mi-popup-body">
          {t("notPatientPopup.body")} <strong>Dr. {doctorName}</strong>.
        </p>
        <button className="mi-popup-btn" onClick={onContinue}>
          {t("notPatientPopup.goToMessaging")}
        </button>
      </div>
    </div>
  );
};
const InitialsAvatar = ({ fullName }) => {
  const initials = getInitials(fullName);
  return (
    <div className="mi-avatar-initials" aria-hidden="true">
      {initials}
    </div>
  );
};

export default function DoctorHeaderCard({
  doctor,
  showEdit = false,
  showActions = true,
  onMessage,
  isPatientOfDoctor = false,
  isDoctorSession = false,
  doctorIdOverride,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [showNotPatientPopup, setShowNotPatientPopup] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const { isFollowing, loading: followLoading, toggleFollow, canFollow } = useDoctorFollow(doctor);

  const mapped = useMemo(() => {
    if (!doctor) return null;

    const fullName =
      `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim() ||
      doctor?.fullName ||
      doctor?.name ||
      "Doctor";

    return {
      fullName,
      specialty: doctor?.specialty || "General Practitioner",
      email: doctor?.email || "",
      phone: doctor?.phone || "",
      about: doctor?.about || doctor?.bio,
      avatar: resolveDoctorAvatar(
        doctor?.avatarUrl ??
        doctor?.avatar ??
        doctor?.profileImage ??
        doctor?.photo ??
        doctor?.raw?.avatarUrl ??
        doctor?.raw?.avatar ??
        doctor?.raw?.photo
      ),
    };
  }, [doctor]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [mapped?.avatar]);

  const specialtyLabel = useMemo(() => {
    const key = normalizeSpecialtyKey(mapped?.specialty);
    return key ? t(`doctorFinder.specialties.${key}`) : mapped?.specialty;
  }, [mapped?.specialty, t]);

  const navigateToDoctorMessaging = () => {
    const doctorId = String(
      doctor?.externalId ??
        doctor?.doctorId ??
        doctor?.medecinId ??
        doctor?.id ??
        "",
    ).trim();
    if (!doctorId) return;

    const contact = {
      id: doctorId,
      userId: doctorId,
      peerId: doctorId,
      name: mapped.fullName,
      fullName: mapped.fullName,
      avatar: mapped.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png",
      photo: mapped.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png",
      meta: {
        type: "doctor",
        role: "doctor",
        doctorId,
        registerId: doctorId,
        displayName: mapped.fullName,
      },
    };

    try {
      sessionStorage.setItem("pendingSelectedContact", JSON.stringify(contact));
    } catch {
      // Messaging can still resolve the doctor from the URL and loaded contacts.
    }

    const messagingRoute = getMessagingRouteForActiveRole();
    navigate(`${messagingRoute}?doctorId=${encodeURIComponent(doctorId)}`);
  };

  const handleMessageClick = () => {
    if (!isDoctorSession && !isPatientOfDoctor) {
      setShowNotPatientPopup(true);
      return;
    }

    if (onMessage && typeof onMessage === "function") {
      onMessage(doctor);
      return;
    }

    const doctorId = doctorIdOverride || getDoctorId(doctor);
    if (!doctorId) {
      console.error("[DoctorHeaderCard] Cannot message doctor without an id", doctor);
      return;
    }

    navigate(isDoctorSession ? MESSAGING_ROUTES.DOCTOR : ROUTES.MESSAGING, {
      state: {
        selectedDoctor: {
          id: doctorId,
          doctorId: doctorId,
          fullName: mapped?.fullName,
          name: mapped?.fullName,
          specialty: mapped?.specialty,
          email: mapped?.email,
          phone: mapped?.phone,
        },
      },
    });
  };

  const getButtonConfig = () => {
    if (followLoading) {
      return {
        text: t("doctorHeaderCard.loading"),
        disabled: true,
        className: "mi-follow mi-follow--loading",
      };
    }

    if (!canFollow) {
      return {
        text: t("doctorHeaderCard.follow"),
        disabled: true,
        className: "mi-follow mi-follow--disabled",
      };
    }

    if (isFollowing) {
      return {
        text: t("doctorHeaderCard.unfollow"),
        disabled: false,
        className: "mi-follow mi-follow--unfollow",
      };
    }

    return {
      text: t("doctorHeaderCard.follow"),
      disabled: false,
      className: "mi-follow mi-follow--follow",
    };
  };

  const buttonConfig = getButtonConfig();

  if (!mapped) return null;

  return (
    <section className="mi-card" role="region" aria-label="Doctor profile header" dir={isRTL ? "rtl" : "ltr"}>

      {showNotPatientPopup && (
        <NotPatientPopup
          doctorName={mapped.fullName}
          onClose={() => setShowNotPatientPopup(false)}
          onContinue={navigateToDoctorMessaging}
        />
      )}

      {showActions && (
        <div className="mi-actions">
          <button
            type="button"
            className="mi-iconbtn"
            aria-label={t("doctorHeaderCard.sendMessage")}
            onClick={handleMessageClick}
            title={t("doctorHeaderCard.message")}
          >
            <MessageIcon />
          </button>

          <button
            type="button"
            className={buttonConfig.className}
            onClick={toggleFollow}
            disabled={buttonConfig.disabled}
            aria-pressed={isFollowing}
            aria-busy={followLoading}
          >
            {buttonConfig.text}
          </button>
        </div>
      )}

      <div className="mi-toprow">
        <div className="mi-left">
          {mapped.avatar && !avatarFailed ? (
            <img
              className="mi-avatar"
              src={mapped.avatar}
              alt={`Dr. ${mapped.fullName}`}
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <InitialsAvatar fullName={mapped.fullName} />
          )}

          <div className="mi-id">
            <h3 className="mi-name">Dr. {mapped.fullName}</h3>
            <div className="mi-sub">{specialtyLabel}</div>

            <div className="mi-contact">
              {mapped.email ? (
                <span className="mi-item">
                  <MailIcon />
                  <a href={`mailto:${mapped.email}`} className="mi-link">
                    {mapped.email}
                  </a>
                </span>
              ) : null}

              {mapped.email && mapped.phone ? <span className="mi-sep">•</span> : null}

              {mapped.phone ? (
                <span className="mi-item">
                  <PhoneIcon />
                  <a href={`tel:${mapped.phone}`} className="mi-link">
                    {mapped.phone}
                  </a>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {mapped.about ? <p className="mi-about">{mapped.about}</p> : null}
    </section>
  );
}